import dayjs, { Dayjs } from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import relativeTime from "dayjs/plugin/relativeTime"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import customParseFormat from "dayjs/plugin/customParseFormat"

dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)

type DateInputType = Dayjs | Date | string | null

export const renewalDateFormat = "D MMM YYYY"

/**
 * Adds ordinal number suffixes (st,nd,rd,th) to day number.
 * @param date - The date to format or the day number.
 * @returns The formatted day eg. '8th'.
 */
export function formatDay(date: Dayjs | number) {
  if (typeof date === "number") {
    return formatDay(dayjs().set("month", 1).set("date", date))
  }
  return date.format("Do")
}

export function formatDate(date?: Dayjs) {
  if (!date) {
    return ""
  }

  return date.format("Do MMM YY")
}

export function formatDateDashed(date: Dayjs) {
  // Warning: this is american format and should be avoided within the UI
  return date.format("YYYY-MM-DD")
}

/**
 * Formats a date as an ISO-8601 offset date-time at midnight UTC, preserving the
 * calendar date (e.g. '2026-03-01T00:00:00Z'). Does NOT convert local→UTC, so the
 * day cannot shift under a non-zero local offset.
 *
 * Use when a backoffice gateway field is typed as OffsetDateTime and a bare
 * 'YYYY-MM-DD' would fail deserialization.
 */
export function formatDateTimeStartOfDayUtc(date: Dayjs) {
  return date.format("YYYY-MM-DD[T00:00:00Z]")
}

export function formatDateSlashed(date: Dayjs) {
  return date.format("DD/MM/YYYY")
}

export function formatDateTime(date: Dayjs) {
  return dayjs(date).format("D MMM YYYY") + " at " + dayjs(date).format("h:mma")
}

export function formatTimeSlot(startTime: Dayjs, endTime: Dayjs) {
  return (
    dayjs(startTime).format("HH:mm") + " - " + dayjs(endTime).format("HH:mm")
  )
}

export function getFullDateAndTime(date: Dayjs) {
  return (
    dayjs(date).format("dddd D MMM YYYY") + " at " + dayjs(date).format("h:mma")
  )
}

export function getFullDate(date: Dayjs) {
  return dayjs(date).format("ddd, D MMM YYYY")
}

export function formatFullDateLong(date: Dayjs) {
  return dayjs(date).format("dddd D MMMM")
}

export function daysFromToday(date: Dayjs) {
  const today = dayjs().startOf("day")
  const compareDate = date.startOf("day")

  return today.diff(compareDate, "day")
}

export function daysAgoFormatted(date: string | Dayjs) {
  date = dayjs.isDayjs(date) ? date : dayjs(date)

  const days = daysFromToday(date)

  if (days === 0) {
    return "Today"
  } else if (days === 1) {
    return "Yesterday"
  } else if (days > 1) {
    return days + " days ago"
  } else if (days === -1) {
    return "Tomorrow"
  } else {
    return "in " + -days + " days"
  }
}

export function daysFromTodayFormatted(date: string | Dayjs) {
  date = dayjs.isDayjs(date) ? date : dayjs(date)

  const days = daysFromToday(date)
  if (days === 0) {
    return "Today"
  } else if (days === 1) {
    return "Yesterday"
  } else if (days > 31) {
    return dayjs(date).format("D MMM YYYY") + " (" + dayjs(date).fromNow() + ")"
  } else if (days > 6) {
    return (
      dayjs(date).format("D MMM YYYY") +
      " (" +
      Math.floor(days / 7) +
      " Weeks Ago" +
      ")"
    )
  } else if (days > 1) {
    return days + " Days Ago"
  } else if (days === -1) {
    return "Tomorrow"
  } else {
    return "in " + -days + " days"
  }
}

export function safeDayjs(date?: DateInputType): Dayjs | undefined {
  return date ? dayjs(date) : undefined
}

export function dayjsOrNull(date?: DateInputType): Dayjs | null {
  return date ? dayjs(date) : null
}

export function isAfterToday(date: Dayjs): boolean {
  return date.isAfter(dayjs(), "day")
}

export function isAmOrPm(date: string | Dayjs): string {
  const hour = dayjs(date).hour()
  return hour < 12 ? "AM" : "PM"
}

export function getMonthName(date: Dayjs) {
  return date.format("MMMM")
}

export function getMonthDays() {
  return Array.from({ length: 28 }, (_, i) => i + 1)
}

export function formatDatePeriod(from: Dayjs, to: Dayjs) {
  return `${from.format("D MMM YYYY")} - ${to.format("D MMM YYYY")}`
}

export function formatServerDate(date?: DateInputType) {
  return date ? dayjs(date).format("YYYY-MM-DD") : null
}

export function formatServerDateTime(date?: DateInputType) {
  return date ? dayjs(date).toISOString() : null
}

export function formatLocalDateTime(date?: DateInputType) {
  return date ? dayjs(date).format("YYYY-MM-DDTHH:mm:ss") : null
}

export function formatServerDateTimeNow() {
  return dayjs().toISOString()
}

export function compareDates(date1: Dayjs, date2: Dayjs, isDesc = true) {
  if (isDesc) {
    return date1.isAfter(date2) ? -1 : 1
  } else {
    return date1.isAfter(date2) ? 1 : -1
  }
}

export function isWithinDateRange(
  date1: DateInputType,
  date2: DateInputType,
  days: number,
): boolean {
  const difference = dayjs(date1).diff(dayjs(date2), "day")
  return Math.abs(difference) <= days
}

export function dayjsUTC(date?: DateInputType) {
  return dayjs.utc(date).tz("Europe/London")
}

export function numberOfDaysInInterval(from: DateInputType, to: DateInputType) {
  return dayjs(to).diff(dayjs(from), "days") + 1
}

export function numberOfWorkingDaysInInterval(
  from: DateInputType,
  to: DateInputType,
) {
  const dateFrom = dayjs(from)
  const dateTo = dayjs(to)
  let dateIterator = dateFrom
  let numberOfDays = 0

  while (dateIterator.isSameOrBefore(dateTo)) {
    if ([6, 0].includes(dateIterator.day())) {
      numberOfDays += 0
    } else if (dateIterator.day(6).isBefore(dateTo)) {
      numberOfDays += 6 - dateIterator.day()
    } else if (dateIterator.day(6).isSame(dateTo)) {
      numberOfDays += 5
    } else {
      numberOfDays += dateTo.day()
    }
    dateIterator = dateIterator.add(1, "week").day(1)
  }

  return numberOfDays
}

export function getDateRange(daysBack: number): { from: string; to: string } {
  return {
    from: dayjs().subtract(daysBack, "days").toISOString(),
    to: dayjs().toISOString(),
  }
}
